import * as React from "react";

import { FOCUS_RING_INSET, cx } from "./controlStyles";
import { TabCount, type TabCountTone } from "./Tabs";

// Segmented control = a VIEW SWITCH or FILTER inside a page (List / Grid,
// Inbox / Sent, Mine / Vault, theme). Page sections use Tabs instead.
//
//   <Segmented ariaLabel="View" value={view} onChange={setView}
//     options={[{ value: "list", icon: <List />, ariaLabel: "List" },
//               { value: "grid", icon: <Grid />, ariaLabel: "Grid" }]} />
//
// Radio-group semantics: one tab stop; Arrow keys move AND select, Home /
// End jump. Icon-only options need `ariaLabel`. The tray is a control
// height — md 32px (28px items), sm 28px (24px items), lg 40px (36px
// items: a page's main choice, like the Record screen's "where from"), 44px
// items on touch screens — so it lines up with buttons and dropdowns beside it;
// the selected item is an accent-tinted chip (bg-selected) with an accent
// edge and primary text, so it never looks like a hovered one.

export interface SegmentedOption<T extends string> {
  value: T;
  label?: React.ReactNode;
  icon?: React.ReactNode;
  /** Required when there is no text label. */
  ariaLabel?: string;
  title?: string;
  count?: number | string;
  /** As on Tabs: "warning" = waiting on you, "attention" = a review queue. */
  countTone?: TabCountTone;
  disabled?: boolean;
}

interface SegmentedProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  ariaLabel: string;
  size?: "sm" | "md" | "lg";
  /** Stretch to the container, items share the width. */
  fullWidth?: boolean;
  className?: string;
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  size = "md",
  fullWidth,
  className,
}: SegmentedProps<T>) {
  const refs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const enabled = options.filter((o) => !o.disabled);
  const selectedIndex = options.findIndex((o) => o.value === value);
  // With nothing selected, the first enabled option takes the tab stop.
  const tabStop = selectedIndex >= 0 ? selectedIndex : options.findIndex((o) => !o.disabled);

  function move(from: number, key: string) {
    if (!enabled.length) return;
    const order = options.map((o, i) => ({ o, i })).filter(({ o }) => !o.disabled);
    const pos = order.findIndex(({ i }) => i === from);
    let next = pos;
    if (key === "ArrowRight" || key === "ArrowDown") next = pos === -1 || pos === order.length - 1 ? 0 : pos + 1;
    if (key === "ArrowLeft" || key === "ArrowUp") next = pos <= 0 ? order.length - 1 : pos - 1;
    if (key === "Home") next = 0;
    if (key === "End") next = order.length - 1;
    const target = order[next];
    if (!target) return;
    refs.current[target.i]?.focus();
    if (target.o.value !== value) onChange(target.o.value);
  }

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cx(
        "items-center gap-0.5 rounded-[calc(var(--r-control)+2px)] bg-inset p-[2px] shadow-[inset_0_0_0_1px_var(--c-border-subtle)]",
        fullWidth ? "flex w-full" : "inline-flex",
        className,
      )}
    >
      {options.map((o, i) => {
        const on = o.value === value;
        const hasCount = o.count !== undefined && o.count !== null && o.count !== "";
        return (
          <button
            key={o.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={o.ariaLabel}
            title={o.title ?? (o.label ? undefined : o.ariaLabel)}
            disabled={o.disabled}
            tabIndex={i === tabStop ? 0 : -1}
            onClick={() => !on && onChange(o.value)}
            onKeyDown={(e) => {
              if (["ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown", "Home", "End"].includes(e.key)) {
                e.preventDefault();
                move(i, e.key);
              }
            }}
            className={cx(
              "inline-flex min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-control font-medium transition-[background-color,color,box-shadow] duration-100",
              size === "sm" ? "h-[24px] px-2 text-xs" : size === "lg" ? "h-[36px] gap-2 px-3 text-sm" : "h-[28px] px-2.5 text-sm",
              "[@media(pointer:coarse)]:h-[44px]",
              !o.label && cx(size === "sm" ? "w-[28px] px-0" : "w-[32px] px-0", "[@media(pointer:coarse)]:w-[44px]"),
              fullWidth && "flex-1",
              on
                ? "bg-selected text-fg shadow-[inset_0_0_0_1px_var(--c-glass-active-accent-border)]"
                : "text-fg-muted [@media(hover:hover)]:[&:not(:disabled)]:hover:text-fg",
              "disabled:cursor-default disabled:opacity-45",
              FOCUS_RING_INSET,
            )}
          >
            {o.icon && (
              <span aria-hidden className={cx("flex shrink-0 items-center", size === "lg" ? "[&>svg]:h-4 [&>svg]:w-4" : "[&>svg]:h-3.5 [&>svg]:w-3.5")}>
                {o.icon}
              </span>
            )}
            {o.label && <span className="truncate">{o.label}</span>}
            {hasCount && (o.countTone && o.countTone !== "default" ? (
              // A toned count looks the same as on a tab, selected or not.
              <TabCount count={o.count!} tone={o.countTone} className="h-[18px] min-w-[18px] px-1" />
            ) : (
              <span
                className={cx(
                  "inline-grid h-[18px] min-w-[18px] place-items-center rounded-pill px-1 text-xs font-semibold tabular-nums",
                  on ? "bg-selected text-accent-fg" : "bg-raised text-fg-muted",
                )}
              >
                {o.count}
              </span>
            ))}
          </button>
        );
      })}
    </div>
  );
}
